import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ToolShell } from "@/components/tools/ToolShell";
import { getDeToolBySlug } from "@/lib/de-tools-catalog";
import { getDeToolContent } from "@/lib/de-tool-content";
import { ToolBody } from "../free-tools/$slug";
import { getEsSlugForEnSlug } from "@/lib/es-tools-catalog";

export const Route = createFileRoute("/de/$slug")({
  loader: ({ params }) => {
    if (params.slug === "pdf-tools") throw redirect({ to: "/de/pdf-tools", statusCode: 301 });
    if (params.slug === "ueber-uns" || params.slug === "über-uns" || params.slug === "about") throw redirect({ to: "/de/about", statusCode: 301 });
    if (params.slug === "kontakt" || params.slug === "contact") throw redirect({ to: "/de/contact", statusCode: 301 });
    if (params.slug === "datenschutzrichtlinie" || params.slug === "privacy-policy") throw redirect({ to: "/de/privacy-policy", statusCode: 301 });
    if (params.slug === "nutzungsbedingungen" || params.slug === "terms-of-use") throw redirect({ to: "/de/terms-of-use", statusCode: 301 });
    if (params.slug === "haftungsausschluss" || params.slug === "disclaimer") throw redirect({ to: "/de/disclaimer", statusCode: 301 });

    const tool = getDeToolBySlug(params.slug);
    if (!tool) throw notFound();
    return { tool };
  },
  head: ({ params }) => {
    const tool = getDeToolBySlug(params.slug);
    if (!tool) return { meta: [{ title: "Tool — Picamini.online" }] };

    const content = getDeToolContent(tool.slug, tool.name);
    const canonicalUrl = `https://picamini.online/de/${tool.slug}`;
    const enUrl = `https://picamini.online/${tool.enSlug}`;
    const esSlug = getEsSlugForEnSlug(tool.enSlug);
    const esUrl = `https://picamini.online/es/${esSlug}`;

    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `${tool.name} - Picamini.online`,
      "url": canonicalUrl,
      "description": content.seoDescription,
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "All",
      "browserRequirements": "Erfordert JavaScript. Erfordert HTML5.",
      "inLanguage": "de",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
      },
      "featureList": content.features || [],
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Startseite",
          "item": "https://picamini.online/de/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": tool.name,
          "item": canonicalUrl,
        },
      ],
    };

    const faqSchema = content.faq && content.faq.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    } : null;

    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify(webAppSchema),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema),
      },
    ];

    if (faqSchema) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(faqSchema),
      });
    }

    return {
      meta: [
        { title: content.seoTitle },
        { name: "description", content: content.seoDescription },
        {
          name: "keywords",
          content: tool.keywords?.join(", ") || `${tool.name.toLowerCase()}, kostenlose tools, picamini`,
        },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:locale", content: "de_DE" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${tool.name} — Kostenloses Tool auf Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: content.seoTitle },
        { name: "twitter:description", content: content.seoDescription },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hrefLang: "de", href: canonicalUrl },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts,
    };
  },
  component: DeToolPage,
});

function DeToolPage() {
  const { tool } = Route.useLoaderData();
  const content = getDeToolContent(tool.slug, tool.name);

  return (
    <ToolShell tool={tool} content={content} lang="de">
      <ToolBody key={tool.enSlug} slug={tool.enSlug} />
    </ToolShell>
  );
}
